/**
 * Section Exit Tracker
 *
 * Tracks active foreground time spent in each AppScreens section and fires
 * `section_exit` when the user leaves the section or backgrounds the app.
 *
 * Usage (once, at app root):
 *   useSectionExitTracker();
 */

import {
  onMounted, onUnmounted, watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { AppScreens } from '@/shared/types';
import { useMainStore } from '@/core/store/main';
import { logAnalyticsEvent } from './analytics';
import { AnalyticsEventName } from './types';

const MIN_ENGAGEMENT_MS = 1_000;

const UNTRACKED_SCREENS = new Set<AppScreens>([
  AppScreens.None,
  AppScreens.IconsPreview,
]);

const isTrackable = (screen: AppScreens): boolean => !UNTRACKED_SCREENS.has(screen);

export const useSectionExitTracker = () => {
  const { activeScreen } = storeToRefs(useMainStore());

  let currentSection: AppScreens | null = null;
  let accumulated = 0;
  let lastTickStart: number | null = null;
  let pauseListener: PluginListenerHandle | null = null;
  let resumeListener: PluginListenerHandle | null = null;

  const pauseAccumulation = () => {
    if (lastTickStart === null) {
      return;
    }

    accumulated += Date.now() - lastTickStart;
    lastTickStart = null;
  };

  const reset = () => {
    currentSection = null;
    accumulated = 0;
    lastTickStart = null;
  };

  const startSection = (section: AppScreens) => {
    if (!isTrackable(section)) {
      reset();

      return;
    }

    currentSection = section;
    accumulated = 0;
    lastTickStart = Date.now();
  };

  const fireSectionExit = async () => {
    if (currentSection === null || !isTrackable(currentSection)) {
      reset();

      return;
    }

    pauseAccumulation();

    const sectionTimeSpentMs = Math.round(accumulated);
    const sectionName = currentSection;

    reset();

    if (sectionTimeSpentMs < MIN_ENGAGEMENT_MS) {
      return;
    }

    await logAnalyticsEvent({
      name: AnalyticsEventName.SectionExit,
      params: {
        section_name: sectionName,
        section_time_spent_ms: sectionTimeSpentMs,
      },
    });
  };

  watch(
    activeScreen,
    async (newSection, oldSection) => {
      if (newSection === oldSection) {
        return;
      }

      await fireSectionExit();
      startSection(newSection);
    },
  );

  startSection(activeScreen.value);

  onMounted(async () => {
    pauseListener = await App.addListener('pause', () => {
      void fireSectionExit();
    });

    resumeListener = await App.addListener('resume', () => {
      startSection(activeScreen.value);
    });
  });

  onUnmounted(() => {
    void pauseListener?.remove();
    void resumeListener?.remove();
    pauseListener = null;
    resumeListener = null;
  });
};
